/**
 * customer-app · 购物车服务（热敏分单）
 *
 * 来源：js/cart-service.js（已迁移）。
 *
 * 职责：在 RestaurantCartLogic 之上提供热敏「按打印机 + 出单方式」分单（纯数据，不发起网络）。
 *   与 js/print-lib.js 中 POST /print 前的分组规则保持一致。
 *
 * 兼容契约（必须保留）：
 *   - `window.RestaurantCartService = Object.assign({}, RestaurantCartLogic, { 3 个新方法 })`
 *     → 同时拥有 cart-logic 的所有方法 + 自己的 3 个；被 print-lib / customer-lite / ui 取用
 *   - `OS.registerModule('cartService', …)` 当 OS 总线就绪时注册
 *   - 须在 cart-logic.js（提供 RestaurantCartLogic）之后加载
 *
 * 设计：
 *   - 3 个新方法用 `export` 显式导出，便于后续单测与跨 TS 模块复用
 *   - `RestaurantCartLogic` 类型来自 `./cart-logic`（P5a-4 已 sharpen）
 *     → 顶层 spread 兼容 fallback `{}`，故对外契约用 `Partial<RestaurantCartLogic>`
 */

import type { RestaurantCartLogic } from './cart-logic';

/* ─────────── 类型 ─────────── */

export type PrintMode = 'single' | 'summary';

/** 单个订单行（开放契约：业务可能扩展更多字段） */
export interface OrderItem {
  category?: string | number | null;
  thermalPrinter?: string | number | null;
  [key: string]: unknown;
}

/** thermal 配置（来自 normalizeThermal） */
export interface ThermalConfig {
  /** 分类 → 出单方式（'single' 一菜一票 / 'summary' 汇总）；可有 'default' 作为兜底 */
  categoryPrintMode?: Record<string, PrintMode | string>;
  /** 分类 → 逻辑打印机名；'_uncat' / 'default' 是兜底键 */
  categoryToPrinter?: Record<string, string>;
  /** 全局兜底出单方式 */
  printMode?: PrintMode | string;
}

/** 分单结果：每组对应一台逻辑打印机 + 一种出单方式 */
export interface PrinterGroup {
  printerLogical: string;
  printMode: PrintMode;
  items: OrderItem[];
}

/** RestaurantCartService 自有的 3 个新方法（不含从 cart-logic 合并来的部分） */
export interface CartServiceMethods {
  effectivePrintModeForCategory(catId: unknown, thermal: ThermalConfig): PrintMode;
  resolvePrinterLogicalForItem(it: OrderItem | null | undefined, printerCfg: Record<string, unknown>): string;
  groupThermalOrderItems(items: OrderItem[] | unknown, thermal: ThermalConfig): PrinterGroup[];
}

/**
 * 完整对外契约：cart-logic 的全部成员 + 自己的 3 个新方法。
 * 用 `Partial<RestaurantCartLogic>` 是因为运行期允许 cart-logic 缺席（fallback `{}`）。
 * entry.js 已保证加载顺序；此处类型只是「失败安全」的兜底。
 */
export type RestaurantCartService = Partial<RestaurantCartLogic> & CartServiceMethods;

/* ─────────── 全局 facade 局部断言 ─────────── */

interface RuntimeGlobal {
  RestaurantCartLogic?: RestaurantCartLogic;
  RestaurantCartService?: RestaurantCartService;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
}

const G = (typeof window !== 'undefined' ? window : globalThis) as unknown as RuntimeGlobal;

const Cart: Partial<RestaurantCartLogic> = G.RestaurantCartLogic ?? {};

/* ─────────── 内部小工具 ─────────── */

/** 把 unknown 视作可能为字符串的值并 trim；非可转字符串返回 '' */
const trimStr = (v: unknown): string => {
  if (v == null) return '';
  return String(v).trim();
};

const isPrintMode = (v: unknown): v is PrintMode => v === 'single' || v === 'summary';

/* ─────────── 3 个新方法 ─────────── */

const effectivePrintModeForCategory = (catId: unknown, thermal: ThermalConfig): PrintMode => {
  const cpm =
    thermal.categoryPrintMode && typeof thermal.categoryPrintMode === 'object'
      ? thermal.categoryPrintMode
      : ({} as Record<string, PrintMode | string>);
  const k = catId != null ? String(catId) : '';
  if (k && isPrintMode(cpm[k])) return cpm[k] as PrintMode;
  if (isPrintMode(cpm.default)) return cpm.default as PrintMode;
  if (isPrintMode(thermal.printMode)) return thermal.printMode as PrintMode;
  return 'summary';
};

const resolvePrinterLogicalForItem = (
  it: OrderItem | null | undefined,
  printerCfg: Record<string, unknown>,
): string => {
  const cat = it && it.category != null ? String(it.category) : '';
  let logical = '';

  /* 1. 行内强制指定的 thermalPrinter 优先 */
  const itPrinter = trimStr(it?.thermalPrinter);
  if (itPrinter) {
    logical = itPrinter;
  } else if (cat) {
    /* 2. 分类 → 打印机映射 */
    const byCat = trimStr(printerCfg[cat]);
    if (byCat) logical = byCat;
  }

  /* 3. 未分类兜底 */
  if (!logical) {
    const uncat = trimStr(printerCfg._uncat);
    if (uncat) logical = uncat;
  }

  /* 4. 全局兜底 */
  if (!logical) {
    const def = trimStr(printerCfg.default);
    if (def) logical = def;
  }

  return logical || 'default';
};

const groupThermalOrderItems = (
  items: OrderItem[] | unknown,
  thermal: ThermalConfig,
): PrinterGroup[] => {
  const list: OrderItem[] = Array.isArray(items) ? (items as OrderItem[]) : [];
  const printerCfg: Record<string, unknown> =
    thermal.categoryToPrinter && typeof thermal.categoryToPrinter === 'object'
      ? (thermal.categoryToPrinter as Record<string, unknown>)
      : {};

  const byKey: Record<string, PrinterGroup> = {};
  list.forEach((it) => {
    const cat = it && it.category != null ? String(it.category) : '';
    const logical = resolvePrinterLogicalForItem(it, printerCfg);
    const mode = effectivePrintModeForCategory(cat, thermal);
    /** key 用 '\0' 作为分隔，避免 logical 名里含特殊字符时碰撞 */
    const key = `${logical}\0${mode}`;
    if (!byKey[key]) byKey[key] = { printerLogical: logical, printMode: mode, items: [] };
    byKey[key].items.push(it);
  });
  return Object.values(byKey);
};

/* ─────────── 暴露：合并 cart-logic + 自己的 3 个方法 ─────────── */

const service: RestaurantCartService = {
  ...Cart,
  effectivePrintModeForCategory,
  resolvePrinterLogicalForItem,
  groupThermalOrderItems,
};

G.RestaurantCartService = service;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('cartService', service);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
